import { get } from 'svelte/store';
import { camera_previews } from 'src/stores/features/cameras/previews';
import { buildCloudMediaUrl } from 'src/services/cam-cloud/recordings';
import { real_id } from 'src/helpers/interface_helpers';
import { computeCameraState } from 'src/components/Camera/helpers/camera-state-helpers';
import { getCameraComponentKey } from 'src/components/Camera/helpers/camera-helpers';
import type { CameraStreamer } from 'src/components/Camera/types/camera-status';
import type {
	DashCameraWidgetConfig,
	DashCameraWidgetFeedMode,
	DashCameraWidgetVariant,
} from './dash-types';
import type { Device } from 'src/types/Device';
import type { DeviceSettingsG2 } from 'src/types/DeviceSettings';
import type { DeviceStatusG2 } from 'src/types/DeviceStatusG2';
import { offlineStateType } from 'src/stores/device-runtime/states';

/** @deprecated Persisted variants normalized on read. */
type LegacyDashCameraWidgetVariant = DashCameraWidgetVariant | 'triple-row' | 'device-control';

export function isTrioCameraVariant(variant?: LegacyDashCameraWidgetVariant): boolean {
	return variant === 'triple-focus' || variant === 'triple-row';
}

/** Full preview and 3-camera layout use a single composite widget. */
export function isSpecialDashCameraLayout(variant?: LegacyDashCameraWidgetVariant): boolean {
	return variant === 'full-preview' || isTrioCameraVariant(variant);
}

/** @deprecated Legacy dashCamera button-card widgets; use dashCardDev for per-camera cards. */
export function isLegacyCameraButtonCardLayout(variant?: LegacyDashCameraWidgetVariant): boolean {
	return variant === 'button-card' || variant === 'device-control';
}

function normalizeCameraVariant(variant?: LegacyDashCameraWidgetVariant): DashCameraWidgetVariant {
	if (variant === 'triple-row') return 'triple-focus';
	if (variant === 'device-control') return 'button-card';
	return variant || 'button-card';
}

export function getCameraSelectionLimit(variant?: LegacyDashCameraWidgetVariant): number {
	if (isTrioCameraVariant(variant)) return 3;
	if (variant === 'full-preview') return 4;
	return 1;
}

/** Ref key used in DashSelectList `allCardsRefs` (variant for dashCamera, else widget type). */
export function getDashWidgetSelectListRefKey(
	widgetType?: string,
	cameraVariant?: DashCameraWidgetVariant,
): string {
	if (widgetType === 'dashCamera' && cameraVariant) {
		return cameraVariant;
	}
	return widgetType || '';
}

/** Apply defaults for missing persisted fields. */
export function normalizeCameraConfig(
	config: DashCameraWidgetConfig = {},
): Required<Pick<DashCameraWidgetConfig, 'variant' | 'feedMode' | 'sideFeedMode' | 'mainAlwaysLive'>> &
	DashCameraWidgetConfig {
	const variant = normalizeCameraVariant(config.variant);
	return {
		...config,
		variant,
		feedMode: config.feedMode ?? 'live',
		sideFeedMode: config.sideFeedMode ?? 'preview',
		mainAlwaysLive: config.mainAlwaysLive ?? true,
	};
}

export function resolveCameraFeedMode(
	cameraId: string | number | undefined | null,
	cameraConfig: DashCameraWidgetConfig = {},
): DashCameraWidgetFeedMode {
	const normalized = normalizeCameraConfig(cameraConfig);
	const key = cameraId != null && cameraId !== '' ? String(cameraId) : '';
	if (key && normalized.cameraFeedModes?.[key]) {
		return normalized.cameraFeedModes[key]!;
	}
	return normalized.feedMode;
}

export function resolveSlotFeedMode(
	role: 'main' | 'side',
	cameraConfig: DashCameraWidgetConfig = {},
	cameraId?: string | number,
): DashCameraWidgetFeedMode {
	if (cameraId != null && cameraId !== '') {
		const perCamera = cameraConfig.cameraFeedModes?.[String(cameraId)];
		if (perCamera) return perCamera;
	}
	const normalized = normalizeCameraConfig(cameraConfig);
	if (role === 'main' && isTrioCameraVariant(normalized.variant)) {
		if (normalized.mainAlwaysLive) return 'live';
		return normalized.feedMode;
	}
	if (role === 'side' && isTrioCameraVariant(normalized.variant)) {
		return normalized.sideFeedMode;
	}
	return normalized.feedMode;
}

export function canMountLivePlayer(
	device: Device | undefined,
	status: DeviceStatusG2 | undefined,
	settings: DeviceSettingsG2 | undefined,
): boolean {
	if (!device || !status?.online) return false;
	const cameraKey = getCameraComponentKey(device);
	const privacy = settings?.[cameraKey]?.privacy ?? false;
	const streamer = (status?.[cameraKey] as { streamer?: CameraStreamer } | undefined)?.streamer;
	return computeCameraState(privacy, true, streamer) === null;
}

/**
 * Resolve a signed cloud preview URL for the given camera device id.
 */
export async function getCameraPreviewUrl(
	deviceId: string | number | undefined | null,
): Promise<string> {
	if (deviceId === undefined || deviceId === null || deviceId === '') return '';
	const id = real_id(String(deviceId));
	const previews = get(camera_previews);
	const baseUrl = previews?.[id]?.large || previews?.[id]?.small;
	if (!baseUrl) return '';
	return buildCloudMediaUrl(baseUrl);
}

export type DashCameraWidgetCardRef = {
	id: string | number;
	_type?: string;
	position?: number;
};

export function getCameraDeviceFromCard(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
): Device | undefined {
	if (!card?.id) return undefined;
	return devices[card.id] as Device | undefined;
}

export function getCameraDeviceStatus(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
	devicesStatus: Record<string, DeviceStatusG2 | undefined>,
): DeviceStatusG2 | undefined {
	const device = getCameraDeviceFromCard(card, devices);
	return device ? devicesStatus[real_id(device.id)] : undefined;
}

export function getCameraDeviceSettings(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
	devicesSettings: Record<string, DeviceSettingsG2 | undefined>,
): DeviceSettingsG2 | undefined {
	const device = getCameraDeviceFromCard(card, devices);
	return device ? devicesSettings[real_id(device.id)] : undefined;
}

export function getCameraCardName(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
): string {
	const device = getCameraDeviceFromCard(card, devices);
	return device?.name || String(card?.id ?? '');
}

export function isCameraCardOffline(
	card: DashCameraWidgetCardRef | undefined,
	devices: Record<string, Device>,
	devicesStatus: Record<string, DeviceStatusG2 | undefined>,
	deviceState: { type?: number } | undefined,
): boolean {
	const device = getCameraDeviceFromCard(card, devices);
	const status = devicesStatus[real_id(device?.id)];
	const state = deviceState;
	return !device || !status?.online || state?.type === offlineStateType;
}

export function isCameraSlotUsingLiveFeed(
	card: DashCameraWidgetCardRef | undefined,
	role: 'main' | 'side',
	cameraConfig: DashCameraWidgetConfig,
	devices: Record<string, Device>,
): boolean {
	const device = getCameraDeviceFromCard(card, devices);
	if (!device || !card?.id) return false;
	return resolveSlotFeedMode(role, cameraConfig, card.id) === 'live';
}

/** Whether a single-camera dashboard device card should mount the live WebRTC feed. */
export function isSingleCameraLiveFeedEnabled(
	deviceId: string | number | undefined | null,
	cameraConfig?: DashCameraWidgetConfig,
): boolean {
	if (cameraConfig == null) return false;
	return resolveCameraFeedMode(deviceId, cameraConfig) === 'live';
}
